import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building2, Users, Wallet, Copy, Share2, CheckCircle2, Clock, BadgeCheck, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const fmtCve = (v) => `${Number(v || 0).toLocaleString('pt-PT')} CVE`;

const STEPS = ['proposal_submitted', 'approved', 'paid'];
const STEP_ICON = { proposal_submitted: Clock, approved: CheckCircle2, paid: BadgeCheck };

const Stat = ({ icon: Icon, label, value, hint, testId, accent }) => (
  <Card className="p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2"><Icon className="h-4 w-4" />{label}</div>
    <div className={`text-3xl font-bold tracking-tight ${accent || 'text-slate-900'}`}>{value}</div>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </Card>
);

export const InfluencerDashboard = ({ invite }) => {
  const { t } = useTranslation();
  const { status, referrals = {}, payout = {}, ref_url, code, invoice_number, admin_note, promo_code, promo_percent, sales } = invite;
  const stepIdx = STEPS.indexOf(status);
  const rejected = status === 'rejected';

  const share = async () => {
    const text = t('influencer.shareText', { url: ref_url });
    if (navigator.share) {
      try { await navigator.share({ title: 'Trabadju.cv', text, url: ref_url }); return; } catch { /* cancelled */ }
    }
    navigator.clipboard?.writeText(ref_url).then(() => toast.success(t('influencer.copied')));
  };

  return (
    <div className="space-y-6" data-testid="influencer-dashboard">
      <header>
        <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-2">{t('influencer.dashEyebrow')}</p>
        <h1 className="text-[28px] sm:text-[34px] font-bold tracking-tighter text-slate-900 leading-[1.1]">{t('influencer.dashTitle', { name: invite.name })}</h1>
        <p className="mt-2 text-[15px] text-slate-600">{t(`influencer.state.${status}.text`)}</p>
        {admin_note && <p className="mt-2 text-sm text-slate-600 italic">{t('influencer.adminNote')}: {admin_note}</p>}
      </header>

      <ol className="flex items-center gap-2" aria-label={t('influencer.progress')} data-testid="influencer-progress">
        {STEPS.map((s, i) => {
          const Icon = rejected && i === 0 ? XCircle : STEP_ICON[s];
          const done = !rejected && i <= stepIdx;
          return (
            <li key={s} className="flex items-center gap-2 flex-1 min-w-0">
              <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${rejected && i === 0 ? 'bg-rose-100 text-rose-700' : done ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'}`}><Icon className="h-4 w-4" /></span>
              <span className={`text-xs sm:text-sm truncate ${done ? 'text-slate-900 font-semibold' : 'text-slate-500'}`}>{t(`influencer.state.${rejected && i === 0 ? 'rejected' : s}.title`)}</span>
              {i < STEPS.length - 1 && <span className={`hidden sm:block h-px flex-1 ${done && i < stepIdx ? 'bg-emerald-600' : 'bg-slate-200'}`} />}
            </li>
          );
        })}
      </ol>

      <div className="grid sm:grid-cols-3 gap-4">
        <Stat icon={Building2} label={t('influencer.statEmployers')} value={referrals.employers ?? 0} hint={payout.rate_employer ? t('influencer.perReg', { v: fmtCve(payout.rate_employer) }) : null} testId="stat-employers" />
        <Stat icon={Users} label={t('influencer.statCandidates')} value={referrals.candidates ?? 0} hint={payout.rate_candidate ? t('influencer.perReg', { v: fmtCve(payout.rate_candidate) }) : null} testId="stat-candidates" />
        <Stat icon={Building2} label={t('influencer.statPaid')} value={referrals.employers_paid ?? 0} hint={t('influencer.statPaidHint', { v: fmtCve(referrals.revenue_cve || 0) })} testId="stat-paid" />
        <Stat icon={Wallet} label={status === 'paid' ? t('influencer.statPaid') : t('influencer.statPayout')} value={fmtCve(payout.total)} accent="text-emerald-700"
          hint={payout.bonus_total ? t('influencer.payoutBreakdown', { base: fmtCve(payout.base), bonus: fmtCve(payout.bonus_total) }) : t('influencer.payoutBase')} testId="stat-payout" />
      </div>

      {promo_code && (
        <Card className="p-6 bg-slate-900 text-white" data-testid="influencer-promo-card">
          <h2 className="font-semibold mb-1">{t('influencer.promoTitle')}</h2>
          <p className="text-sm text-slate-300 mb-4">{t('influencer.promoHint', { pct: promo_percent })}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <code className="text-2xl font-bold tracking-[0.2em] bg-slate-800 rounded-md px-4 py-2 text-amber-300" data-testid="influencer-promo-code">{promo_code}</code>
            <Button variant="outline" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white" onClick={() => navigator.clipboard?.writeText(promo_code).then(() => toast.success(t('influencer.copied')))} data-testid="influencer-copy-promo"><Copy className="h-4 w-4 mr-1" />{t('influencer.copy')}</Button>
            <span className="sm:ml-auto text-sm text-emerald-300" data-testid="influencer-promo-sales">{t('influencer.promoSales', { n: sales?.count || 0, v: fmtCve(sales?.revenue_cve || 0) })}</span>
          </div>
        </Card>
      )}

      <Card className="p-6" data-testid="influencer-ref-card">
        <h2 className="font-semibold text-slate-900 mb-1">{t('influencer.refTitle')}</h2>
        <p className="text-sm text-slate-600 mb-4">{t('influencer.refHint')}</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <code className="flex-1 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-900 break-all" data-testid="influencer-ref-url">{ref_url}</code>
          <Button variant="outline" onClick={() => navigator.clipboard?.writeText(ref_url).then(() => toast.success(t('influencer.copied')))} data-testid="influencer-copy-ref"><Copy className="h-4 w-4 mr-1" />{t('influencer.copy')}</Button>
          <Button onClick={share} data-testid="influencer-share-ref"><Share2 className="h-4 w-4 mr-1" />{t('influencer.share')}</Button>
        </div>
        <p className="mt-3 text-xs text-slate-500">{t('influencer.codeLabel')}: <span className="font-mono font-semibold">{code}</span>{invoice_number ? ` · ${invoice_number}` : ''}</p>
      </Card>
    </div>
  );
};
